import QRCode from 'qrcode';

/**
 * "Media Share" QR card: a square 1080×1080 image an agent puts on screen during
 * a TikTok Live (or posts anywhere) so viewers can scan straight into the
 * share link. Drawn on a canvas from the "3D Sitemap QR Card" HTML template —
 * same geometry, colours and type — so the PNG is identical on every device
 * (html-to-image screenshots drift on WebViews and mid-load fonts).
 */

export interface MediaCardAgent {
  name: string;
  ceaNumber: string;
  mobile: string;
}

const SIZE = 1080;
/** Output scale: 2160 px PNG, crisp on retina screens and screen shares. */
const SCALE = 2;

const BG = '#0B1220';
const INK = '#F5F1EA';
const ORANGE = '#F28C38';
const META = '#D3DAE8';
const PANEL = '#131C30';
const QR_BG = '#FFFDF8';

const DISPLAY_FONT = '"Bricolage Grotesque", "DM Sans", system-ui, sans-serif';
const BODY_FONT = '"DM Sans", system-ui, sans-serif';
const FONTS_CSS = 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,700;12..96,800&family=DM+Sans:wght@500;700&display=swap';

let fontsReady: Promise<void> | null = null;

/** Loads the template's Google Fonts once; falls back to system sans after 4s. */
function loadFonts(): Promise<void> {
  if (!fontsReady) {
    // fonts.load() resolves empty until the @font-face rules exist, so wait for the stylesheet first.
    const stylesheet = new Promise<void>((resolve) => {
      const existing = document.querySelector<HTMLLinkElement>(`link[href="${FONTS_CSS}"]`);
      if (existing?.sheet) {
        resolve();
        return;
      }
      const link = existing ?? document.createElement('link');
      link.addEventListener('load', () => resolve(), { once: true });
      link.addEventListener('error', () => resolve(), { once: true });
      if (!existing) {
        link.rel = 'stylesheet';
        link.href = FONTS_CSS;
        document.head.appendChild(link);
      }
    });
    const load = stylesheet
      .then(() => Promise.all([
        document.fonts.load(`800 112px "Bricolage Grotesque"`, 'SCAN HERE 3D'),
        document.fonts.load(`700 29px "DM Sans"`, 'Huttons'),
      ]))
      .then(() => undefined);
    const timeout = new Promise<void>((resolve) => { setTimeout(resolve, 4000); });
    fontsReady = Promise.race([load, timeout]).catch(() => undefined);
  }
  return fontsReady;
}

/** `+65 9123 4567` for a Singapore mobile; anything else as given. */
export function formatCardMobile(raw: string): string {
  const digits = (raw || '').replace(/\D/g, '');
  const local = digits.length === 10 && digits.startsWith('65') ? digits.slice(2) : digits;
  return local.length === 8 ? `+65 ${local.slice(0, 4)} ${local.slice(4)}` : raw.trim();
}

/** `TAN KOK SOON` / `tan kok soon` → `Tan Kok Soon`; also capitalises after `-` and `'` (Lee-Ann, O'Neil). */
export function toNameCase(raw: string): string {
  return raw
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase()
    .split(/([ \-'])/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('');
}

// --- Text helpers (manual letter-spacing: ctx.letterSpacing isn't in older Safari) ---

function spacedWidth(ctx: CanvasRenderingContext2D, text: string, spacing: number): number {
  let w = 0;
  for (const ch of text) w += ctx.measureText(ch).width + spacing;
  return text ? w - spacing : 0;
}

function drawSpaced(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, spacing: number): number {
  let cx = x;
  for (const ch of text) {
    ctx.fillText(ch, cx, y);
    cx += ctx.measureText(ch).width + spacing;
  }
  return cx - spacing;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

/** Largest font size ≤ `max` whose spaced text fits `maxWidth`. */
function fitFont(ctx: CanvasRenderingContext2D, text: string, weight: number, family: string, max: number, maxWidth: number, spacing = 0): number {
  let size = max;
  for (; size > 16; size -= 2) {
    ctx.font = `${weight} ${size}px ${family}`;
    if (spacedWidth(ctx, text, spacing) <= maxWidth) break;
  }
  return size;
}

// --- Isometric skyline (template SVG, viewBox 60 20 900 550) ---

const SKYLINE: Array<[string, string]> = [
  ['416,120 485,160 485,340 416,300', '#1B2740'], ['589,100 485,160 485,340 589,280', '#131C30'], ['520,60 589,100 485,160 416,120', '#2D3F63'],
  ['762,145 840,190 840,390 762,345', '#1B2740'], ['918,145 840,190 840,390 918,345', '#131C30'], ['840,100 918,145 840,190 762,145', '#2D3F63'],
  ['213,90 300,140 300,400 213,350', '#C8671C'], ['387,90 300,140 300,400 387,350', '#9E4E12'], ['300,40 387,90 300,140 213,90', '#F28C38'],
  ['562,105 657,160 657,480 562,425', '#1B2740'], ['735,115 657,160 657,480 735,435', '#131C30'], ['640,60 735,115 657,160 562,105', '#2D3F63'],
  ['99,295 177,340 177,460 99,415', '#1B2740'], ['238,305 177,340 177,460 238,425', '#131C30'], ['160,260 238,305 177,340 99,295', '#2D3F63'],
  ['711,400 797,450 797,560 711,510', '#1B2740'], ['867,410 797,450 797,560 867,520', '#131C30'], ['780,360 867,410 797,450 711,400', '#2D3F63'],
  ['343,390 447,450 447,540 343,480', '#1B2740'], ['534,400 447,450 447,540 534,490', '#131C30'], ['430,340 534,400 447,450 343,390', '#2D3F63'],
];

function drawSkyline(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  // preserveAspectRatio="xMidYMid meet"
  const scale = Math.min(w / 900, h / 550);
  const ox = x + (w - 900 * scale) / 2;
  const oy = y + (h - 550 * scale) / 2;
  ctx.save();
  ctx.translate(ox, oy);
  ctx.scale(scale, scale);
  ctx.translate(-60, -20);
  ctx.lineJoin = 'round';
  ctx.lineWidth = 2;
  ctx.strokeStyle = BG;
  for (const [points, fill] of SKYLINE) {
    const coords = points.split(' ').map((p) => p.split(',').map(Number));
    ctx.beginPath();
    coords.forEach(([px, py], i) => (i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py)));
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
}

/** A 24×24 stroke icon (template SVG paths) drawn at `size`. */
function drawIcon(ctx: CanvasRenderingContext2D, paths: string[], x: number, y: number, size: number, strokeWidth: number): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(size / 24, size / 24);
  ctx.strokeStyle = ORANGE;
  ctx.lineWidth = strokeWidth;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  paths.forEach((d) => ctx.stroke(new Path2D(d)));
  ctx.restore();
}

const ARROW_PATHS = ['M4 12h16', 'M13 5l7 7-7 7'];
const PHONE_PATHS = ['M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.8a16 16 0 0 0 6 6l1.2-1.2a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z'];

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load QR image'));
    img.src = src;
  });
}

/**
 * Splits `text` into at most `maxLines` lines that fit `maxWidth` at the current
 * font. Returns null when the words don't fit (caller shrinks the font).
 */
function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number, maxLines: number): string[] | null {
  const lines: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width <= maxWidth) {
      line = next;
    } else {
      if (!line) return null;
      lines.push(line);
      line = word;
      if (ctx.measureText(line).width > maxWidth) return null;
    }
  }
  if (line) lines.push(line);
  return lines.length <= maxLines ? lines : null;
}

/** Project name, right-aligned in the top-right corner beside the pill and above the title, with an orange underline. */
function drawProjectName(ctx: CanvasRenderingContext2D, projectName: string, right: number, top: number): void {
  const name = projectName.trim().replace(/\s+/g, ' ').toUpperCase();
  if (!name) return;
  const maxWidth = 380;
  let size = 44;
  let lines: string[] | null = null;
  for (; size >= 24; size -= 2) {
    ctx.font = `800 ${size}px ${DISPLAY_FONT}`;
    lines = wrapLines(ctx, name, maxWidth, 2);
    if (lines) break;
  }
  if (!lines) {
    // A single word too long even at 24px: shrink it onto one line.
    size = fitFont(ctx, name, 800, DISPLAY_FONT, 24, maxWidth);
    ctx.font = `800 ${size}px ${DISPLAY_FONT}`;
    lines = [name];
  }

  ctx.save();
  ctx.font = `800 ${size}px ${DISPLAY_FONT}`;
  ctx.fillStyle = INK;
  ctx.textAlign = 'right';
  const lineH = Math.round(size * 1.02);
  const firstBaseline = top + 4 + size * 0.86;
  lines.forEach((line, i) => ctx.fillText(line, right, firstBaseline + i * lineH));

  ctx.fillStyle = ORANGE;
  ctx.fillRect(right - 40, firstBaseline + (lines.length - 1) * lineH + 16, 40, 4);
  ctx.restore();
}

/** Renders the card for `url` and returns it as a PNG blob. */
export async function renderMediaShareCard(url: string, agent: MediaCardAgent, projectName = ''): Promise<Blob> {
  await loadFonts();
  const qrImage = await loadImage(await QRCode.toDataURL(url, {
    width: 340 * SCALE,
    margin: 0,
    errorCorrectionLevel: 'M',
    color: { dark: BG, light: QR_BG },
  }));

  const canvas = document.createElement('canvas');
  canvas.width = SIZE * SCALE;
  canvas.height = SIZE * SCALE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not available');
  ctx.scale(SCALE, SCALE);
  ctx.textBaseline = 'alphabetic';

  // Background + 72px blueprint grid.
  ctx.fillStyle = BG;
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.fillStyle = 'rgba(255,255,255,0.045)';
  for (let p = 0; p < SIZE; p += 72) {
    ctx.fillRect(p, 0, 2, SIZE);
    ctx.fillRect(0, p, SIZE, 2);
  }

  const left = 64;
  const right = SIZE - 64;

  // "GET FREE" pill.
  ctx.font = `800 34px ${DISPLAY_FONT}`;
  const pillText = 'GET FREE';
  const pillW = spacedWidth(ctx, pillText, 6) + 60;
  const pillH = 60;
  const pillY = 56;
  ctx.fillStyle = ORANGE;
  roundRect(ctx, left, pillY, pillW, pillH, pillH / 2);
  ctx.fill();
  ctx.fillStyle = BG;
  drawSpaced(ctx, pillText, left + 30, pillY + 42, 6);

  drawProjectName(ctx, projectName, right, pillY);

  // Title: "3D SITEMAP" / "REPORT", 112px at 0.92 line height.
  ctx.font = `800 112px ${DISPLAY_FONT}`;
  const line1 = pillY + pillH + 20 + 92;
  ctx.fillStyle = ORANGE;
  const after3d = drawSpaced(ctx, '3D', left, line1, -2);
  ctx.fillStyle = INK;
  drawSpaced(ctx, ' SITEMAP', after3d - 2, line1, -2);
  drawSpaced(ctx, 'REPORT', left, line1 + 103, -2);

  // Scan row: prompt + skyline on the left, QR card on the right.
  const rowY = line1 + 103 + 11 + 28;
  const qrCardSize = 340 + 64;
  const qrCardX = right - 12 - qrCardSize;
  const qrCardY = rowY;

  ctx.fillStyle = ORANGE; // hard offset shadow
  roundRect(ctx, qrCardX + 12, qrCardY + 12, qrCardSize, qrCardSize, 40);
  ctx.fill();
  ctx.fillStyle = QR_BG;
  roundRect(ctx, qrCardX, qrCardY, qrCardSize, qrCardSize, 40);
  ctx.fill();
  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(qrImage, qrCardX + 32, qrCardY + 32, 340, 340);
  ctx.imageSmoothingEnabled = true;

  const leftBlockH = 72 + 8 + 287;
  const leftY = rowY + (qrCardSize - leftBlockH) / 2;
  ctx.font = `800 60px ${DISPLAY_FONT}`;
  ctx.fillStyle = INK;
  const scanEnd = drawSpaced(ctx, 'SCAN HERE', left, leftY + 58, 4);
  drawIcon(ctx, ARROW_PATHS, scanEnd + 20, leftY + 10, 52, 3);
  drawSkyline(ctx, left, leftY + 80, 470, 287);

  // Agent panel.
  const panelY = rowY + qrCardSize + 28;
  const panelW = right - left;
  const panelH = 24 + 54 + 12 + 38 + 24;
  ctx.fillStyle = PANEL;
  roundRect(ctx, left, panelY, panelW, panelH, 32);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.16)';
  ctx.lineWidth = 2;
  roundRect(ctx, left + 1, panelY + 1, panelW - 2, panelH - 2, 31);
  ctx.stroke();

  const innerW = panelW - 72;
  const name = toNameCase(agent.name || '');
  const nameSize = fitFont(ctx, name, 800, DISPLAY_FONT, 54, innerW);
  ctx.font = `800 ${nameSize}px ${DISPLAY_FONT}`;
  ctx.fillStyle = INK;
  ctx.textAlign = 'center';
  ctx.fillText(name, SIZE / 2, panelY + 24 + 46);
  ctx.textAlign = 'left';

  // Meta line: agency | CEA | phone, separated by orange bars.
  const parts = ['Huttons Asia Pte Ltd', agent.ceaNumber, formatCardMobile(agent.mobile)].filter(Boolean);
  const hasPhone = !!agent.mobile;
  const sepGap = 22;
  const iconW = 28 + 10;
  let metaSize = 29;
  const metaWidth = (size: number) => {
    ctx.font = `700 ${size}px ${BODY_FONT}`;
    const text = parts.reduce((w, p) => w + ctx.measureText(p).width, 0);
    return text + (parts.length - 1) * (sepGap * 2 + 3) + (hasPhone ? iconW : 0);
  };
  while (metaSize > 18 && metaWidth(metaSize) > innerW) metaSize -= 1;
  ctx.font = `700 ${metaSize}px ${BODY_FONT}`;
  const metaBaseline = panelY + 24 + 54 + 12 + 30;
  let x = (SIZE - metaWidth(metaSize)) / 2;
  ctx.font = `700 ${metaSize}px ${BODY_FONT}`;
  parts.forEach((part, i) => {
    if (i > 0) {
      x += sepGap;
      ctx.fillStyle = ORANGE;
      ctx.fillRect(x, metaBaseline - 24, 3, 28);
      x += 3 + sepGap;
    }
    if (hasPhone && i === parts.length - 1) {
      drawIcon(ctx, PHONE_PATHS, x, metaBaseline - 25, 28, 2.4);
      x += iconW;
    }
    ctx.fillStyle = META;
    ctx.fillText(part, x, metaBaseline);
    x += ctx.measureText(part).width;
  });

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Failed to encode image'))), 'image/png');
  });
}
